/**
 * Planning authority transitions via transactional RPCs (build contract §2.3a).
 * All durable state changes for plan version, evaluation, POR, divergence, tradeoff,
 * and reprioritization trigger go through these RPCs. No direct insert/update for
 * authority state from the client.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTenantContext } from './tenantContext';

const RPC_CREATE_PLAN_VERSION = 'create_plan_version';
const RPC_EVALUATE_PLAN_VERSION = 'evaluate_plan_version';
const RPC_AUTHORIZE_POR = 'authorize_por';
const RPC_RECORD_DIVERGENCE = 'record_divergence';
const RPC_RECORD_TRADEOFF = 'record_tradeoff';
const RPC_OPEN_REPRIORITIZATION_TRIGGER = 'open_reprioritization_trigger';
const RPC_CREATE_REPRIORITIZATION_RECOMMENDATION = 'create_reprioritization_recommendation';
const RPC_UPDATE_REPRIORITIZATION_TRIGGER_STATUS = 'update_reprioritization_trigger_status';
const RPC_RESOLVE_DIVERGENCE = 'resolve_divergence';
const RPC_DETECT_PLAN_DIVERGENCES = 'detect_plan_divergences';

/** Result of an RPC that returns a single UUID. */
export type RpcUuidResult = { data: string | null; error: string | null };

/**
 * 1) Create an immutable plan version. Payload must be the full snapshot (jsonb).
 * Returns the new plan_version id. engine_version (Doc 18 §6.2) stored when provided.
 */
export async function createPlanVersionRpc(params: {
  plan_cycle_id: string;
  payload: Record<string, unknown> | unknown[];
  change_reason?: string | null;
  created_by?: string | null;
  engine_version?: string | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const createdBy = params.created_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_CREATE_PLAN_VERSION, {
      p_plan_cycle_id: params.plan_cycle_id,
      p_payload: params.payload,
      p_change_reason: params.change_reason ?? null,
      p_created_by: createdBy,
      p_engine_version: params.engine_version ?? null,
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'create_plan_version failed',
    };
  }
}

/** Single check payload for atomic evaluate_plan_version RPC (Phase 2). */
export interface EvaluatePlanVersionCheck {
  check_key: string;
  result: string;
  payload?: Record<string, unknown> | null;
}

/**
 * 2) Persist a constraint evaluation atomically (evaluation row + check results + audit).
 * Returns the plan_constraint_evaluation id. Phase 2 hardening: single transactional RPC.
 */
export async function evaluatePlanVersionRpc(params: {
  plan_version_id: string;
  engine_version: string;
  evaluated_by?: string | null;
  summary: Record<string, unknown>;
  details: Record<string, unknown>;
  checks: EvaluatePlanVersionCheck[];
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const evaluatedBy = params.evaluated_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_EVALUATE_PLAN_VERSION, {
      p_plan_version_id: params.plan_version_id,
      p_engine_version: params.engine_version,
      p_evaluated_by: evaluatedBy,
      p_summary: params.summary as Record<string, unknown>,
      p_details: params.details as Record<string, unknown>,
      p_checks: params.checks.map((c) => ({
        check_key: c.check_key,
        result: c.result,
        payload: c.payload ?? {},
      })),
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'evaluate_plan_version failed',
    };
  }
}

/**
 * 3) Authorize a plan version as the new Plan of Record. Supersedes the current
 * active POR for that cycle. Snapshot payload is taken from plan_versions.payload,
 * enriched with next_right_ranking when provided (Doc 108 §5.4).
 * Evidence linkage (Doc 18 §6.3): constraint_evaluation_id, divergence_ids, tradeoff_decision_ids.
 * Returns the new plan_of_record_snapshot id.
 */
export async function authorizePorRpc(params: {
  plan_version_id: string;
  authorized_by?: string | null;
  constraint_evaluation_id?: string | null;
  divergence_ids?: string[] | null;
  tradeoff_decision_ids?: string[] | null;
  /**
   * Next-Right Work Unit ranking computed at POR authorization time (Doc 108 §5.4).
   * Persisted in plan_of_record_snapshots.payload.next_right_ranking.
   */
  next_right_ranking?: Array<{
    work_unit_id: string;
    title: string;
    score: number;
    top_positive_drivers?: string[];
    top_penalties?: string[];
  }> | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const authorizedBy = params.authorized_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_AUTHORIZE_POR, {
      p_plan_version_id: params.plan_version_id,
      p_authorized_by: authorizedBy,
      p_constraint_evaluation_id: params.constraint_evaluation_id ?? null,
      p_divergence_ids: params.divergence_ids ?? null,
      p_tradeoff_decision_ids: params.tradeoff_decision_ids ?? null,
      p_next_right_ranking: params.next_right_ranking ?? null,
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'authorize_por failed',
    };
  }
}

/**
 * 4) Record a rank/sequence divergence for a plan version (Doc 18 §4.1).
 * resolution_status defaults to 'detected'; use resolve_divergence for accept/mitigate/dispute.
 * Returns the rank_sequence_divergence id.
 */
export async function recordDivergenceRpc(params: {
  plan_version_id: string;
  payload_json: Record<string, unknown>;
  caused_by_evaluation_id?: string | null;
  resolution_status?: string | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc(RPC_RECORD_DIVERGENCE, {
      p_plan_version_id: params.plan_version_id,
      p_payload_json: params.payload_json,
      p_caused_by_evaluation_id: params.caused_by_evaluation_id ?? null,
      p_resolution_status: params.resolution_status ?? 'detected',
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'record_divergence failed',
    };
  }
}

/**
 * 5) Record a tradeoff decision for a plan version.
 * Returns the tradeoff_decision id.
 */
export async function recordTradeoffRpc(params: {
  plan_version_id: string;
  payload_json: Record<string, unknown>;
  tradeoff_kind?: string;
  accepted_by?: string | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const acceptedBy = params.accepted_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_RECORD_TRADEOFF, {
      p_plan_version_id: params.plan_version_id,
      p_payload_json: params.payload_json,
      p_tradeoff_kind: params.tradeoff_kind ?? 'other',
      p_accepted_by: acceptedBy,
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'record_tradeoff failed',
    };
  }
}

/**
 * 6a) Create a reprioritization recommendation (status=recommended). Does not update plan_cycles.
 * Returns the reprioritization_trigger id. Link to evidence via plan_constraint_evaluation_id.
 */
export async function createReprioritizationRecommendationRpc(params: {
  plan_cycle_id: string;
  reason_json: Record<string, unknown>;
  triggered_by?: string | null;
  plan_constraint_evaluation_id?: string | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const triggeredBy = params.triggered_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_CREATE_REPRIORITIZATION_RECOMMENDATION, {
      p_plan_cycle_id: params.plan_cycle_id,
      p_reason_json: params.reason_json,
      p_triggered_by: triggeredBy,
      p_plan_constraint_evaluation_id: params.plan_constraint_evaluation_id ?? null,
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'create_reprioritization_recommendation failed',
    };
  }
}

/**
 * 6b) Open a reprioritization trigger for a plan cycle (governance "return to prioritization").
 * Updates plan_cycles; inserts trigger with status 'open'. Returns the reprioritization_trigger id.
 */
export async function openReprioritizationTriggerRpc(params: {
  plan_cycle_id: string;
  reason_json: Record<string, unknown>;
  triggered_by?: string | null;
  plan_constraint_evaluation_id?: string | null;
}): Promise<RpcUuidResult> {
  try {
    const supabase = getSupabaseClient();
    const triggeredBy = params.triggered_by ?? getTenantContext()?.userId ?? null;
    const { data, error } = await supabase.rpc(RPC_OPEN_REPRIORITIZATION_TRIGGER, {
      p_plan_cycle_id: params.plan_cycle_id,
      p_reason_json: params.reason_json,
      p_triggered_by: triggeredBy,
      p_plan_constraint_evaluation_id: params.plan_constraint_evaluation_id ?? null,
    });
    if (error) return { data: null, error: error.message };
    const id = data as string | null;
    return { data: id ?? null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'open_reprioritization_trigger failed',
    };
  }
}

/**
 * 6c) Update reprioritization trigger status (acknowledged, closed, etc.).
 */
export async function updateReprioritizationTriggerStatusRpc(params: {
  trigger_id: string;
  status: string;
}): Promise<{ data: null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc(RPC_UPDATE_REPRIORITIZATION_TRIGGER_STATUS, {
      p_trigger_id: params.trigger_id,
      p_status: params.status,
    });
    if (error) return { data: null, error: error.message };
    return { data: null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'update_reprioritization_trigger_status failed',
    };
  }
}

/**
 * Settle a rank_sequence_divergence, with a reason (migration 1027). The one way
 * to change a resolution; it takes the authority that authorizes the plan.
 */
export async function resolveDivergenceRpc(params: {
  divergence_id: string;
  resolution_status: string;
  reason: string;
}): Promise<{ data: null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc(RPC_RESOLVE_DIVERGENCE, {
      p_divergence_id: params.divergence_id,
      p_resolution_status: params.resolution_status,
      p_reason: params.reason,
    });
    if (error) return { data: null, error: error.message };
    return { data: null, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'resolve_divergence failed',
    };
  }
}

/**
 * Record what a plan version shows that differs from its rank (migration 1027).
 * Safe to run again: a version is immutable and each condition is recorded once.
 * Returns how many were newly recorded.
 */
export async function detectPlanDivergencesRpc(params: {
  plan_version_id: string;
  caused_by_evaluation_id?: string | null;
}): Promise<{ data: number; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc(RPC_DETECT_PLAN_DIVERGENCES, {
      p_plan_version_id: params.plan_version_id,
      p_caused_by_evaluation_id: params.caused_by_evaluation_id ?? null,
    });
    if (error) return { data: 0, error: error.message };
    return { data: (data as number | null) ?? 0, error: null };
  } catch (e) {
    return {
      data: 0,
      error: e instanceof Error ? e.message : 'detect_plan_divergences failed',
    };
  }
}
